import { currentUserQuery } from "@common/api"
import { dispositionsQuery } from "@common/api/quoting/disposition"
import { quoteQuery } from "@common/api/quoting/quote"
import { tenantQuotingSettingsQuery } from "@common/api/quoting/settings"
import { taskGroupsQuery } from "@common/api/quoting/task-group"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useTrackEvent } from "@common/hooks/use-track-event"
import { hasPermission } from "@common/lib/permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { useEffect, useRef } from "react"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { AddPartButton } from "./-components/add-part-button"
import { AddTaskButton } from "./-components/add-task-button"
import { ITEM_SIZING, ItemsTable } from "./-components/items-table"
import { NewPhaseButton } from "./-components/phase-actions"
import { RunChecksButton } from "./-components/playbooks-tab"
import { QuoteTab } from "./-components/quote-tab"
import { QuoteTabs } from "./-components/quote-tabs"
import { ReviewActions, ViewSentButton } from "./-components/review-actions"
import { ReviewStrip } from "./-components/review-strip"
import {
  hasTaskFilters,
  NO_TASK_FILTERS,
  taskFilterConfigs,
} from "./-lib/filters"
import {
  COLUMN_LABELS,
  columnsOffered,
  columnsSearch,
  columnsShown,
  displayOf,
  GROUPING_LABELS,
  groupingSearch,
  groupingsOf,
  type QuoteSearch,
  quoteSearchSchema,
} from "./-lib/items"
import { isPartsQuote } from "./-lib/parts-quote"
import { usePhasesEnabled } from "./-lib/phases"
import { tabsFor } from "./-lib/tabs"
import { useTaskGroupsReadable } from "./-lib/task-groups"
import { travellerOf } from "./-lib/traveller"

const INDEX_ROUTE = "/_layout/quotes_/$quoteId/"
const SEND_ROUTE = "/_layout/quotes_/$quoteId/send"
const SENT_ROUTE = "/_layout/quotes_/$quoteId/sent"
const DIALOG_ROUTES: ReadonlySet<string> = new Set([
  "/_layout/quotes_/$quoteId/phases/new",
  "/_layout/quotes_/$quoteId/phases/$phaseId/rename",
  "/_layout/quotes_/$quoteId/phases/$phaseId/delete",
  "/_layout/quotes_/$quoteId/tasks/from-template",
])

export const Route = createFileRoute("/_layout/quotes_/$quoteId")({
  validateSearch: (search) => quoteSearchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "quote:read", "/")
  },
  loader: async ({ context: { queryClient }, params: { quoteId } }) => {
    void queryClient.prefetchQuery(taskGroupsQuery({ deleted: false }))
    const me = await queryClient.ensureQueryData(currentUserQuery())
    const [quote] = await Promise.all([
      queryClient.ensureQueryData(quoteQuery({ quote_id: quoteId })),
      hasPermission(me, "quote_disposition:read") &&
        queryClient.prefetchQuery(dispositionsQuery()),
      hasPermission(me, "quote_settings:read") &&
        queryClient.prefetchQuery(tenantQuotingSettingsQuery()),
    ])
    return {
      breadcrumb: [
        { label: "Quotes", href: "/quotes" },
        { label: quote.number },
      ],
    }
  },
  component: QuotePage,
})

function QuotePage(): React.ReactElement {
  const { quoteId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { data: quote } = useSuspenseQuery(quoteQuery({ quote_id: quoteId }))
  const trackEvent = useTrackEvent()
  const trackedQuote = useRef<string>(undefined)
  const trackedTab = useRef<string>(undefined)
  const phasesEnabled = usePhasesEnabled()
  const display = displayOf(search, quote, phasesEnabled)
  const groupsReadable = useTaskGroupsReadable()
  const departmentsReadable = useHasPermission("department:read")
  const commentsReadable = useHasPermission("quote_comment:read")
  const offered = columnsOffered(quote, {
    groups: groupsReadable,
    departments: departmentsReadable,
    comments: commentsReadable,
  })
  const columns = columnsShown(search, offered)
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(columns, ITEM_SIZING)
  const fit = fitColumns(columns, width, sizing)
  const canReadChecks = useHasPermission("quote_ruleset_evaluation:read")
  const tabs = tabsFor(quote, canReadChecks)
  const tab = search.tab && tabs.includes(search.tab) ? search.tab : "items"
  const pane = useRouterState({
    select: (state) => state.matches.at(-1)?.routeId,
  })
  const open =
    pane === SEND_ROUTE ||
    pane === SENT_ROUTE ||
    (pane !== INDEX_ROUTE &&
      !(pane && DIALOG_ROUTES.has(pane)) &&
      tab === "items")
  const wide =
    open &&
    (search.doc !== undefined ||
      (pane === SEND_ROUTE && search.preview === true))
  const filters = { status: search.status, due: search.due }
  const filterConfigs = taskFilterConfigs(quote)
  const update = (patch: Partial<QuoteSearch>) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })

  useEffect(() => {
    if (trackedQuote.current === quote.id) return
    trackedQuote.current = quote.id
    trackEvent("quote_opened", {
      quote_id: quote.id,
      quote_kind: quote.kind,
      quote_disposition: quote.disposition,
      ...(quote.work_package_id && { work_package_id: quote.work_package_id }),
    })
  }, [quote, trackEvent])

  useEffect(() => {
    if (trackedTab.current === tab) return
    trackedTab.current = tab
    trackEvent("quote_tab_opened", { quote_id: quote.id, tab })
  }, [quote.id, tab, trackEvent])

  return (
    <ListPage
      toolbar={
        <ListToolbar
          tabs={<QuoteTabs quoteId={quote.id} tab={tab} tabs={tabs} />}
          actions={
            <>
              {tab === "items" && (
                <>
                  <FilterButton
                    active={hasTaskFilters(filters)}
                    configs={filterConfigs}
                    onChange={update}
                  />
                  <DisplayOptions
                    groupings={groupingsOf(phasesEnabled)}
                    labels={GROUPING_LABELS}
                    group={display.group}
                    subgroup={display.subgroup}
                    onChange={(next) =>
                      navigate({
                        to: ".",
                        search: (previous) => ({
                          ...previous,
                          ...groupingSearch(next, quote, phasesEnabled),
                        }),
                        replace: true,
                      })
                    }
                  >
                    <ColumnChips
                      options={offered}
                      labels={COLUMN_LABELS}
                      fixed={[]}
                      columns={columns}
                      hidden={fit.hidden}
                      onChange={(next) => update(columnsSearch(next, offered))}
                    />
                  </DisplayOptions>
                  {!travellerOf(quote) && (
                    <>
                      {isPartsQuote(quote) && <AddPartButton quote={quote} />}
                      {phasesEnabled && !isPartsQuote(quote) && (
                        <NewPhaseButton quote={quote} />
                      )}
                      {!isPartsQuote(quote) && <AddTaskButton quote={quote} />}
                    </>
                  )}
                </>
              )}
              {tab === "playbooks" && <RunChecksButton quote={quote} />}
              <ViewSentButton quote={quote} />
              <ReviewActions quote={quote} />
            </>
          }
        />
      }
      pane={wide ? "full" : open ? "split" : "closed"}
      detail={<Outlet />}
      scrollRef={ref}
    >
      {tab === "items" ? (
        <>
          <ActiveFilterBar
            active={hasTaskFilters(filters)}
            configs={filterConfigs}
            values={filters}
            onChange={update}
            onClear={() => update(NO_TASK_FILTERS)}
          />
          <ReviewStrip quote={quote} />
          <ItemsTable
            quote={quote}
            display={display}
            filters={filters}
            shown={fit.shown}
            onClear={() => update(NO_TASK_FILTERS)}
          />
        </>
      ) : (
        <QuoteTab quote={quote} tab={tab} />
      )}
    </ListPage>
  )
}
